import { useCallback, useMemo, useState } from 'react';
import { apiService, addObjection, addFAQ } from '../services/apiService';
import { useConfirm } from '../components/ui/ConfirmDialog';

/**
 * The post-call timeline's New Inquiry actions, for a Playbook Live training
 * session: add the question to the knowledge base (as a new objection/FAQ or a
 * variation, via SaveInquiryModal) or dismiss it. Either marks it processed on
 * the TRAINING session; the dashboard's New Inquiry totals read live calls
 * only, so nothing here is counted there.
 */
export interface InquiryRecord {
  question?: string;
  answer?: string | null;
  category?: string;
  isProcessed?: boolean;
}

type KnowledgeBaseType = 'objection' | 'faq';
interface EditedInquiry { question: string; answer: string; category: string }

const COPY = {
  dismissTitle: 'Mark as processed without saving?',
  dismissMessage: 'This hides the inquiry from this session, but it will NOT be saved to the FAQs or Objections.',
  dismissConfirm: 'Mark as processed',
  dismissed: 'Inquiry marked as processed',
  saved: (type: KnowledgeBaseType) => `Added to the ${type === 'objection' ? 'objections' : 'FAQs'} knowledge base`,
  savedNotMarked: ' (it could not be marked as processed on this session)',
  markFailed: 'Could not mark the inquiry as processed. Please try again.',
  saveFailed: 'Could not add the inquiry to the knowledge base. Please try again.',
} as const;

interface Options {
  /** The training session's id, as the admin endpoints take it. */
  sessionId?: string;
  newInquiries?: InquiryRecord[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function useTrainingInquiryActions({ sessionId, newInquiries, onSuccess, onError }: Options) {
  const confirm = useConfirm();
  const [markedNow, setMarkedNow] = useState<Set<string>>(new Set());
  const [savingQuestion, setSavingQuestion] = useState<string | null>(null);
  const [activeInquiry, setActiveInquiry] = useState<InquiryRecord | null>(null);

  const processed = useMemo(() => {
    const questions = new Set(markedNow);
    (newInquiries ?? []).forEach((inquiry) => {
      if (inquiry?.isProcessed && inquiry?.question) questions.add(inquiry.question);
    });
    return questions;
  }, [markedNow, newInquiries]);

  /** Persists "processed"; throws so each caller can word its own failure. */
  const persistProcessed = useCallback(async (question: string) => {
    await apiService.put(`/training/admin/sessions/${sessionId}/inquiry-processed`, { question });
    setMarkedNow((previous) => new Set([...previous, question]));
  }, [sessionId]);

  /** Used by the modal after "use existing" / "add as variation": no second confirmation. */
  const markProcessed = useCallback(async (inquiry: InquiryRecord) => {
    if (!inquiry?.question) return;
    try {
      await persistProcessed(inquiry.question);
    } catch (err) {
      console.error('Failed to mark training inquiry as processed:', err);
      onError(COPY.markFailed);
    }
  }, [persistProcessed, onError]);

  const dismiss = useCallback(async (inquiry: InquiryRecord) => {
    const isConfirmed = await confirm({
      title: COPY.dismissTitle,
      message: COPY.dismissMessage,
      confirmLabel: COPY.dismissConfirm,
      variant: 'destructive',
    });
    if (!isConfirmed || !inquiry?.question) return;
    try {
      await persistProcessed(inquiry.question);
      onSuccess(COPY.dismissed);
    } catch (err) {
      console.error('Failed to mark training inquiry as processed:', err);
      onError(COPY.markFailed);
    }
  }, [confirm, persistProcessed, onSuccess, onError]);

  const save = useCallback(async (inquiry: InquiryRecord, type: KnowledgeBaseType, edited: EditedInquiry) => {
    const question = inquiry?.question ?? '';
    setSavingQuestion(question);
    try {
      if (type === 'objection') {
        await addObjection(edited.question, edited.answer, edited.category, true, null);
      } else {
        await addFAQ(edited.question, edited.answer, edited.category);
      }
    } catch (err) {
      console.error('Failed to add training inquiry to the knowledge base:', err);
      onError(COPY.saveFailed);
      setSavingQuestion(null);
      return;
    }
    try {
      await persistProcessed(question);
      onSuccess(COPY.saved(type));
    } catch (err) {
      console.error('Saved to the knowledge base but could not mark as processed:', err);
      onSuccess(COPY.saved(type) + COPY.savedNotMarked);
    }
    setSavingQuestion(null);
    setActiveInquiry(null);
  }, [persistProcessed, onSuccess, onError]);

  return {
    isProcessed: (question?: string) => Boolean(question && processed.has(question)),
    savingQuestion,
    activeInquiry,
    openSave: setActiveInquiry,
    closeSave: () => setActiveInquiry(null),
    dismiss,
    markProcessed,
    save,
  };
}
